import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular-ivy';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CompanySettingsChanged, ExtCustomCompanySettings } from 'src/app/store/auth/auth.actions';
import { AuthState } from 'src/app/store/auth/auth.state';
import { AuthCompany } from 'src/models';
import { AmplitudeService } from '../amplitude.service';
import { CompanyService } from '../company/company.service';

const TWO_MINUTES_MS = 2 * 60 * 1000;
const FIVE_MINUTES_MS = 5 * 60 * 1000;
/** Delays above this overflow `setTimeout` and fire immediately. */
const MAX_TIMEOUT_MS = 2147483647;

type BookCallReadyFlag = 'bookCallReady2MinTracked' | 'bookCallReady5MinTracked';
type BookCallReadyEvent = 'book_call_ready_2_minutes' | 'book_call_ready_5_minutes';

interface BookCallReadyMilestone {
  thresholdMs: number;
  untilMs: number;
  eventName: BookCallReadyEvent;
  flag: BookCallReadyFlag;
}

const BOOK_CALL_READY_MILESTONES: readonly BookCallReadyMilestone[] = [
  {
    thresholdMs: TWO_MINUTES_MS,
    untilMs: FIVE_MINUTES_MS,
    eventName: 'book_call_ready_2_minutes',
    flag: 'bookCallReady2MinTracked',
  },
  {
    thresholdMs: FIVE_MINUTES_MS,
    untilMs: Number.POSITIVE_INFINITY,
    eventName: 'book_call_ready_5_minutes',
    flag: 'bookCallReady5MinTracked',
  },
];

/**
 * Fires one-time Amplitude events 2 and 5 minutes after onboarding completes.
 * Each event is stored on company settings so it is sent at most once per company.
 * Repeat `track()` calls for the same schedule leave the pending timers in place.
 */
@Injectable({
  providedIn: 'root',
})
export class BookCallReadyService implements OnDestroy {
  private readonly bookCallReadyTracked = new Set<string>();
  private bookCallReadyTimerIds: ReturnType<typeof setTimeout>[] = [];
  /** Null when no schedule is active. Repeat calls with the same key skip timer resets. */
  private activeScheduleKey: string | null = null;
  /** Retries an in-window milestone after Amplitude finishes booting. */
  private amplitudeReadySubscription: Subscription | null = null;

  constructor(
    private readonly store: Store,
    private readonly amplitude: AmplitudeService,
    private readonly companyService: CompanyService,
  ) {}

  ngOnDestroy(): void {
    this.clear();
  }

  clear(): void {
    for (const timerId of this.bookCallReadyTimerIds) {
      clearTimeout(timerId);
    }
    this.bookCallReadyTimerIds = [];
    this.activeScheduleKey = null;
    this.amplitudeReadySubscription?.unsubscribe();
    this.amplitudeReadySubscription = null;
  }

  track(company: AuthCompany | null | undefined): void {
    const completedAtMs = this.completionMs(company);
    const key = company && completedAtMs != null ? this.scheduleKeyFor(company, completedAtMs) : null;
    if (key === this.activeScheduleKey) {
      return;
    }

    this.clear();
    if (!company || completedAtMs == null) {
      return;
    }

    const elapsed = Date.now() - completedAtMs;
    const stored = this.store.selectSnapshot(AuthState.company);
    for (const milestone of BOOK_CALL_READY_MILESTONES) {
      if (this.isBookCallReadyTracked(company, milestone.flag, stored)) {
        continue;
      }
      if (elapsed >= milestone.thresholdMs && elapsed < milestone.untilMs) {
        this.fireBookCallReady(company, milestone.eventName, milestone.flag, stored);
      } else if (elapsed < milestone.thresholdMs) {
        this.scheduleBookCallReady(company, milestone.thresholdMs - elapsed);
      }
    }

    this.activeScheduleKey = this.scheduleKeyFor(company, completedAtMs);
  }

  private completionMs(company: AuthCompany | null | undefined): number | null {
    const completedOnBoardingAt = company?.companySettings?.custom?.completedOnBoardingAt;
    if (!company?.id
      || completedOnBoardingAt == null
      || completedOnBoardingAt === ''
      || completedOnBoardingAt === 'undefined') {
      return null;
    }
    const completedAtMs = new Date(completedOnBoardingAt).getTime();
    return Number.isFinite(completedAtMs) ? completedAtMs : null;
  }

  private scheduleKeyFor(company: AuthCompany, completedAtMs: number): string {
    const ready = this.amplitude.isReady$.value ? '1' : '0';
    const tracked = BOOK_CALL_READY_MILESTONES.map(milestone => (
      this.milestoneTracked(company, milestone.flag) ? '1' : '0'
    )).join('');
    return `${company.id}|${completedAtMs}|${ready}|${tracked}`;
  }

  private milestoneTracked(company: AuthCompany, flag: BookCallReadyFlag): boolean {
    return this.bookCallReadyTracked.has(this.bookCallReadyKey(company.id, flag))
      || company.companySettings?.custom?.[flag] === true;
  }

  private isBookCallReadyTracked(
    company: AuthCompany,
    flag: BookCallReadyFlag,
    stored: AuthCompany | null | undefined,
  ): boolean {
    if (this.milestoneTracked(company, flag)) {
      return true;
    }
    return stored?.id === company.id && stored.companySettings?.custom?.[flag] === true;
  }

  private fireBookCallReady(
    company: AuthCompany,
    eventName: BookCallReadyEvent,
    flag: BookCallReadyFlag,
    stored: AuthCompany | null | undefined,
  ): void {
    if (this.isBookCallReadyTracked(company, flag, stored)) {
      return;
    }
    // Leave the milestone unmarked until the SDK is ready. Persisting it first
    // makes the later ready emission look like a duplicate and drops the event.
    if (this.amplitude.isReady$.value !== true) {
      this.deferUntilAmplitudeReady(company);
      return;
    }
    this.bookCallReadyTracked.add(this.bookCallReadyKey(company.id, flag));
    const custom = company.companySettings?.custom;
    if (custom && !Object.isFrozen(custom)) {
      custom[flag] = true;
    }
    if (stored?.id === company.id && stored.companySettings) {
      const changes: ExtCustomCompanySettings = {};
      changes[flag] = true;
      this.store.dispatch(new CompanySettingsChanged({}, {}, changes));
    }
    this.amplitude.trackEvent(eventName);
    const payload = {
      [`custom.${flag}`]: true,
    } as unknown as Parameters<CompanyService['putCompanySettings']>[1];
    this.companyService.putCompanySettings(company.id, payload).pipe(take(1)).subscribe({
      error: (error: unknown) => {
        Sentry.captureException(error, {
          tags: { component: 'BookCallReadyService', method: 'fireBookCallReady' },
          extra: { companyId: company.id, eventName },
        });
      },
    });
  }

  private deferUntilAmplitudeReady(company: AuthCompany): void {
    if (this.amplitudeReadySubscription) {
      return;
    }
    this.amplitudeReadySubscription = this.amplitude.isReady$.pipe(
      filter(ready => ready === true),
      take(1),
    ).subscribe(() => {
      this.amplitudeReadySubscription = null;
      this.activeScheduleKey = null;
      this.track(company);
    });
  }

  private scheduleBookCallReady(company: AuthCompany, delayMs: number): void {
    if (!(delayMs > 0) || delayMs > MAX_TIMEOUT_MS) {
      return;
    }
    const timerId = setTimeout(() => {
      this.bookCallReadyTimerIds = this.bookCallReadyTimerIds.filter(id => id !== timerId);
      this.activeScheduleKey = null;
      this.track(company);
    }, delayMs);
    this.bookCallReadyTimerIds.push(timerId);
  }

  private bookCallReadyKey(companyId: string, flag: BookCallReadyFlag): string {
    return `${companyId}:${flag}`;
  }
}
